export enum STAGE {
  EMAIL_VERIFICATION = 'EMAIL_VERIFICATION',
  COMPANY_INFORMATION = 'COMPANY_INFORMATION',
  PERSONAL_INFORMATION = 'PERSONAL_INFORMATION',
  REFERRAL_SCREEN = 'REFERRAL_SCREEN', // special case,
  PLAN_SELECTION = 'PLAN_SELECTION',
  IMPLEMENTATION_OPTIONS = 'IMPLEMENTATION_OPTIONS',
  EMBURSE_CARDS = 'EMBURSE_CARDS',
  RSM_PARTNER_REFERRAL_SCREEN = 'RSM_PARTNER_REFERRAL_SCREEN',
  BUSINESS_VERIFICATION = 'BUSINESS_VERIFICATION',
  SUBMIT_APPLICATION = 'SUBMIT_APPLICATION',
  PAYMENT_SUMMARY = 'PAYMENT_SUMMARY',
  FINISH = 'FINISH'
}

export type STEP = Extract<
  STAGE,
  | STAGE.COMPANY_INFORMATION
  | STAGE.PERSONAL_INFORMATION
  | STAGE.PLAN_SELECTION
  | STAGE.EMBURSE_CARDS
  | STAGE.RSM_PARTNER_REFERRAL_SCREEN
  | STAGE.FINISH
>;

type Step = {
  id: STEP;
  stages: STAGE[];
  label?: string;
  content?: string[];
};

const _NEW_COMPANY_REQUIRED_STEPS: Step[] = [
  {
    label: 'Company Information',
    content: ['Provide basic account information'],
    stages: [STAGE.COMPANY_INFORMATION],
    id: STAGE.COMPANY_INFORMATION
  },
  {
    label: 'Personal Information',
    content: ['Provide personal details'],
    stages: [STAGE.PERSONAL_INFORMATION],
    id: STAGE.PERSONAL_INFORMATION
  }
];

const _ADDITIONAL_COMPANY_REQUIRED_STEPS = _NEW_COMPANY_REQUIRED_STEPS.filter(({ id }) => id === STAGE.COMPANY_INFORMATION);

export function getAvailableStepsData(
  isFromSales: boolean,
  cards: string,
  skipReferralCards?: boolean,
  isAdditionalCompany?: boolean,
  isRsmPartner?: boolean
) {
  const isInternalBilling =
    window.FEATURES.stripe_billing || (!window.FEATURES.external_billing && !window.ORG?.is_manual_billing_contract);
  const showPayment = !isRsmPartner && !isFromSales && isInternalBilling;
  const steps = !isAdditionalCompany ? [..._NEW_COMPANY_REQUIRED_STEPS] : [..._ADDITIONAL_COMPANY_REQUIRED_STEPS];
  let optionalSteps = {
    addCards: true,
    addPayment: showPayment,
    planSelection: true
  };

  if (isRsmPartner || isAdditionalCompany) {
    optionalSteps.planSelection = false;
  }

  if ((cards === 'hide' && !skipReferralCards) || isAdditionalCompany) {
    optionalSteps.addCards = false;
  }

  if (optionalSteps.planSelection) {
    steps.push({
      label: 'Plan Selection',
      content: ['Select your plan', 'Implement options'],
      stages: [STAGE.PLAN_SELECTION, STAGE.IMPLEMENTATION_OPTIONS],
      id: STAGE.PLAN_SELECTION
    });
  }

  if (optionalSteps.addCards) {
    steps.push({
      label: 'Emburse Cards',
      content: ['Apply for Cards', 'Business verification', 'Submit Application'],
      stages: [STAGE.EMBURSE_CARDS, STAGE.BUSINESS_VERIFICATION, STAGE.SUBMIT_APPLICATION],
      id: STAGE.EMBURSE_CARDS
    });
  }

  if (isRsmPartner) {
    steps.push({
      stages: [STAGE.RSM_PARTNER_REFERRAL_SCREEN],
      id: STAGE.RSM_PARTNER_REFERRAL_SCREEN
    });
  }

  if (optionalSteps.addPayment) {
    steps.push({
      label: 'Finish',
      content: ['Review and Pay', 'Start using Emburse Spend'],
      stages: [STAGE.PAYMENT_SUMMARY, STAGE.FINISH],
      id: STAGE.FINISH
    });
  } else {
    steps.push({
      label: 'Finish',
      content: ['Start using Emburse Spend'],
      stages: [STAGE.FINISH],
      id: STAGE.FINISH
    });
  }

  let stageToStepMapping = steps.reduce((acc: Record<string, number>, step: any, index: number) => {
    if (isFromSales && step.id === STAGE.PLAN_SELECTION) {
      return acc;
    }
    if (skipReferralCards && step.id === STAGE.EMBURSE_CARDS) {
      return acc;
    }
    const stages = step.stages;
    stages.forEach((stage: number) => {
      acc[stage] = index;
    });
    return acc;
  }, {});

  if (isFromSales) {
    let idToFind = STAGE.EMBURSE_CARDS;
    if (skipReferralCards) {
      idToFind = STAGE.FINISH;
    }
    const index = steps.findIndex((step) => step.id === idToFind);
    stageToStepMapping[STAGE.REFERRAL_SCREEN] = index;
  }

  return {
    steps,
    stageToStepMapping
  };
}
